import { observer } from 'mobx-react-lite';
import './index.scss';
import XImg from '@assets/svg/x.svg';
import { Context } from '@/index';
import { useContext, useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import { SELECT_STYLES, PRICE_FOR_CHOICES, toastOptions } from '@shared/index';
import { updateTask as updateTaskApi, uploadFiles as uploadFilesApi, uploadUrls as uploadUrlsApi } from '@api/task-api';
import { AttachFilesAndLinks } from '@Components/AttachFilesAndLinks';
import config from '@/config';
import { toast } from 'react-toastify';
import ToggleButton from 'react-bootstrap/ToggleButton';
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup';
import { getBasename } from '@utils/utils';

// ----------------
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
// ----------------
const predefinedDeadlineValues = [24, 72, 168];

const validationSchema = Yup.object().shape({
    title: Yup.string()
        .required('Tapşırığın adını daxil edin')
        .min(6, 'Ad ən azı 6 simvol olmalıdır'),
    description: Yup.string()
        .required('Tapşırığın təsvirini daxil edin')
        .min(16, 'Təsvir ən azı 16 simvol olmalıdır'),
    category: Yup.object()
        .required('Kateqoriya seçin'),
    price: Yup.number()
        .typeError('Rəqəm daxil edin')
        .required('Məbləği daxil edin')
        .min(1000, 'Büdcə ən azı 1000 rubl olmalıdır'),
    price_for: Yup.object()
        .required('Tarif seçin'),
    deadline: Yup.number()
        .typeError('Rəqəm daxil edin')
        .required('Saat sayını daxil edin')
        .min(1, 'Müddət ən azı 1 saat olmalıdır'),               
});

const formOptions = { resolver: yupResolver(validationSchema), shouldUnregister: true }

const EditTaskModal = observer(({ taskId, task, programmingLanguage, callGetTask }) => {
    const { modal, tasksCategoriesStore, programmingLanguagesStore, userStore } = useContext(Context)
    const [ selectedProgrammingLanguages, setSelectedProgrammingLanguages ] = useState([]);
    const [ isCustomDeadline, setIsCustomDeadline ] = useState(false);
    const [ attachmentFiles, setAttachmentFiles ] = useState([]);
    const [ isDisabled, setIsDisabled ] = useState(false);
    const {
        setValue,
        register,
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm(formOptions);

    useEffect(() => {
        let defaultValues = {};
        defaultValues.title = task.title;
        defaultValues.description = task.description;
        defaultValues.price = task.price;
        defaultValues.category = task.category;
        defaultValues.price_for = task.price_for === 'project' ?  { value: 'project', label: 'Layihə' } : { value: 'hour', label: 'Saat' };
        if (task.task_type === 1) {
            defaultValues.deadline = task.deadline;
            if(predefinedDeadlineValues.indexOf(task.deadline) === -1) {
                setIsCustomDeadline(true);
            }
        }
        
        JSON.parse(programmingLanguage).map((pl) => {
            selectProgrammingLanguage(pl);
        });
        if (task && task.document_urls && Array.isArray(task.document_urls)) {
            task.document_urls.forEach((documentUrl) => {
                if (!documentUrl) return;
                
                fetch( `${config.strapi.image_url}${documentUrl}`)
                    .then(res => { 
                        if (!res.ok) {
                            throw new Error(`Failed to fetch ${documentUrl}: ${res.status}`);
                        }
                        return res.blob(); 
                    })
                    .then(blob => {
                        let file = new File([blob], getBasename(documentUrl), { type: blob.type });
                        setAttachmentFiles(files => [...files, file]);
                    })
                    .catch((error) => {
                        console.error(`Error fetching file ${documentUrl}:`, error);
                    });
            });
        }
        reset({ ...defaultValues });
    }, []);
    const selectedFilesRef = useRef([]);
    const selectedLinksRef = useRef([]);
    const [ isUploading, setIsUploading ] = useState(false);
    const buttonSpinner = useRef(null);

    useEffect(() => {
        if(isUploading) {
            setIsDisabled(true);
            buttonSpinner.current.style.display = "inline-block";
        } else {
            setIsDisabled(false);
            buttonSpinner.current.style.display = "none";
        }
    }, [isUploading]);

    const onSubmit = (task) => {
        console.log(task);
        console.log(errors);

        task.price_for = task.price_for.value;
        task.category = task.category.id;
        task.programming_language = selectedProgrammingLanguages;
        task.task_type = userStore.isFreelancer ? 2 : 1;
        setIsUploading(true);
        updateTaskApi(taskId, task)
        .then( async (res) => {
            const [
                uploadTaskUrlsResult,
                uploadTaskFilesResult
            ] = await Promise.allSettled([
                (res && res.data && selectedLinksRef.current.length) ? uploadUrlsApi({task: res.data.id, url: selectedLinksRef.current}) : Promise.resolve(),
                (res && res.data && selectedFilesRef.current.length) ? uploadFilesApi(res.data.id, selectedFilesRef.current) : Promise.resolve(),
            ]);
            if (uploadTaskUrlsResult.status === 'fulfilled' && uploadTaskFilesResult.status === 'fulfilled'){
                toast.success(`${task.task_type === 1 ? 'Sifariş redaktə edildi' : 'Xidmət redaktə edildi'} uğurla`, toastOptions);
            } else {
                toast.error(`${task.task_type === 1 ? 'Sifariş yaradıldı' : 'Xidmət yaradıldı'} xətalar ilə`, toastOptions);
            }
            if (uploadTaskUrlsResult.status === 'rejected') {
                toast.error(`${task.task_type === 1 ? 'Sifarişə' : 'Xidmətə'} link əlavə etməkdə xəta`, toastOptions);
            }
            if (uploadTaskFilesResult.status === 'rejected') {
                toast.error(`${task.task_type === 1 ? 'Sifarişə' : 'Xidmətə'} fayl əlavə etməkdə xəta`, toastOptions);
            }
            setIsUploading(false);
            if (callGetTask) {
                callGetTask();
            }
            modal.closeModal();
        })
        .catch(() => {
            setIsUploading(false);
            if (callGetTask) {
                callGetTask();
            }
            toast.error(`${task.task_type === 1 ? 'Sifariş' : 'Xidmət'} yaratmaqda xəta`, toastOptions);
            modal.closeModal();
        });
    }

    const selectProgrammingLanguage = (pl) => {
        if (selectedProgrammingLanguages.includes(pl.id)) {
            let temp = selectedProgrammingLanguages.filter(v => v != pl.id);
            setSelectedProgrammingLanguages(temp);
        } else {
            setSelectedProgrammingLanguages(selectedProgrammingLanguages => [...selectedProgrammingLanguages, pl.id]);
        }
    }

    const setIsCustomDeadlineHandler = () => {
        setIsCustomDeadline(true);
    }

    useEffect(() => {
        isCustomDeadline && predefinedDeadlineValues.indexOf(task.deadline) >= 0 && setValue('deadline', "");
    }, [isCustomDeadline]);

    const onFilesOrLinksChange = (files, links, isSizeLimit) => {
        selectedFilesRef.current = files;
        selectedLinksRef.current = links;
        setIsDisabled(isSizeLimit);
    }

    return (
        <div className="edit-task-modal">
                
            <div className="main">
                <div className="header-modal">
                    { userStore.isFreelancer ? 'Xidməti redaktə et 😎' : 'Sifarişi redaktə et 😎' }
                    <div className="x-block" onClick={() => modal.closeModal()}>
                        <img src={XImg} className="x"/>
                    </div>
                </div>
                <form className="form" onSubmit={handleSubmit(onSubmit)}>
                    <div className="form-control-block">
                        <label htmlFor="title" className="form-label">
                            { userStore.isFreelancer ? 'Xidmətin adı' : 'Sifarişin adı' }
                        </label>
                        <input type="text" className="form-control" id="title" 
                        placeholder={ userStore.isFreelancer ? 'Xidmətin adını daxil edin' : 'Məsələn, saytın əsas səhifəsinin dizaynını hazırlamaq...' }
                        {...register("title")}/>
                        {errors.title?.message && (
                           <p className="error">{errors.title?.message}</p>
                        )}
                    </div>
                    <div className="form-control-block">
                        <label htmlFor="description" className="form-label">
                            { userStore.isFreelancer ? 'Xidmətin təsviri' : 'Nə etmək lazımdır?' }
                        </label>
                        <textarea className="form-control" id="description" rows="4" 
                        placeholder={ userStore.isFreelancer ?
                            'Hansı xidməti göstərdiyinizi və Sifarişçinin nəticədə nə əldə edəcəyini mümkün qədər ətraflı və dəqiq təsvir etməyə çalışın.'  : 
                            "Məsələn, saytın əsas səhifəsi üçün müasir, sadə və asan qavranılan dizayn hazırlamaq lazımdır. Daha ətraflı texniki tapşırıq əlavədə..." } 
                        {...register("description")}></textarea>
                        {errors.description?.message && (
                           <p className="error">{errors.description?.message}</p>
                        )}
                    </div>
                    <AttachFilesAndLinks onChangeCallback={onFilesOrLinksChange} files={attachmentFiles} links={task.urls} editMode={true} />
                    <div className="form-control-block">
                        <label htmlFor="task_category" className="form-label">
                            { userStore.isFreelancer ? 'Xidmət hansı kateqoriyaya aiddir?' : 'Sifariş hansı kateqoriyaya aiddir?' }
                        </label>
                        <Controller
                            name="category"
                            control={control}
                            rules={{ required: true }}
                            render = {({ field: { onChange, onBlur, value, ref } })=> (
                                <Select
                                    fullWidth
                                    placeholder="Kateqoriya seçin"
                                    styles={SELECT_STYLES}
                                    options={tasksCategoriesStore.items}
                                    getOptionLabel={(t) => t.name}
                                    getOptionValue={(t) => t.id}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    defaultValue={task.category}
                                />
                            )}
                        />
                        {errors.category?.message && (
                           <p className="error">{errors.category?.message}</p>
                        )}
                    </div>
                    <div className="form-control-block">
                        <label htmlFor="task_category" className="form-label">Bir və ya bir neçə proqramlaşdırma dili seçin</label>
                        <div className="selected-filters-block">
                        {
                            programmingLanguagesStore.items.map(pl => {
                                return (
                                    <button 
                                        type="button" 
                                        key={pl.id} className={'button ' + (selectedProgrammingLanguages.includes(pl.id) ? 'blue' : '')} 
                                        onClick={() => selectProgrammingLanguage(pl)}
                                    >
                                        {pl.name}
                                    </button>
                                )
                            })
                        }
                        </div>
                    </div>
                    <div className="row">
                        <div className={`${task.task_type === 1 ? 'col-md-6' : 'col-md-12'}`}>
                            <div className="form-control-block">
                                <label className="form-label">
                                    { userStore.isFreelancer ? "Xidmətin büdcəsini müəyyənləşdirin" : "Sifarişin büdcəsini müəyyənləşdirin" }
                                </label>
                                <div className="row">
                                    <div className="col-5 padding-right-zero">
                                        <input type="number" className="form-control" placeholder="1000 AZN"  {...register("price")}/>
                                        {errors.price?.message && (
                                            <p className="error">{errors.price?.message}</p>
                                        )}
                                    </div>
                                    <div className="col-2 vertically-middle padding-right-zero padding-left-zero">
                                        üçün
                                    </div>
                                    <div className="col-5 padding-left-zero">
                                        <Controller
                                            name="price_for"
                                            control={control}
                                            rules={{ required: true }}
                                            render = {({ field: { onChange, onBlur, value, ref } })=> (
                                                <Select
                                                    fullWidth
                                                    placeholder="Nə?"
                                                    styles={SELECT_STYLES}
                                                    options={PRICE_FOR_CHOICES}
                                                    defaultValue={task.price_for === 'project' ?  { value: 'project', label: 'Layihə' } : { value: 'hour', label: 'Saat' }}
                                                    onChange={onChange}
                                                    onBlur={onBlur}
                                                />
                                            )}
                                        />
                                        {errors.price_for?.message && (
                                            <p className="error">{errors.price_for?.message}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {task.task_type === 2 && setValue('deadline', 1)  }
                        {task.task_type === 1 &&
                            <div className="col-md-6">
                                <div className="form-control-block">
                                    <label htmlFor="duration" className="form-label">
                                        { userStore.isFreelancer ? "İcra müddətini göstərin" : "İstədiyiniz icra müddətini göstərin" }
                                    </label>
                                    { isCustomDeadline ? (
                                        <div>
                                            <input type="number" className="form-control" defaultValue="" placeholder="Saatla" {...register("deadline")}/>
                                        </div>
                                    ) : (
                                        <Controller
                                            name="deadline"
                                            control={control}
                                            render = {({ field: { onChange, onBlur, value, ref } })=> (
                                                <ToggleButtonGroup
                                                    className="btn-group radio-group"
                                                    type="radio"
                                                    name="deadline"
                                                    value={value}
                                                    onChange={onChange}
                                                    defaultValue={task.deadline}
                                                >
                                                    <ToggleButton className="btn btn-outline-primary radio-button" id="tbg-btn-1" value={24}>
                                                        24 saat
                                                    </ToggleButton>
                                                    <ToggleButton className="btn btn-outline-primary radio-button" id="tbg-btn-2" value={72}>
                                                        3 gün
                                                    </ToggleButton>
                                                    <ToggleButton className="btn btn-outline-primary radio-button" id="tbg-btn-3" value={168}>
                                                        7 gün
                                                    </ToggleButton>
                                                    <ToggleButton className="btn btn-outline-primary radio-button" id="tbg-btn-4" onClick={setIsCustomDeadlineHandler}>
                                                        başqa
                                                    </ToggleButton>
                                                </ToggleButtonGroup>
                                            )}
                                        />
                                    )}
                                    {errors.deadline?.message && (
                                        <p className="error">{errors.deadline?.message}</p>
                                    )}
                                </div>
                            </div>
                        }
                    </div>
                    <div className="invalid-feedback">
                        Zəhmət olmasa bütün sahələri düzgünlüyünə yoxlayın
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <button type="button" className="my-button second-button" onClick={() => modal.closeModal()}>Ləğv et</button>
                        </div>
                        <div className="col-md-6">
                            <button type="submit" className="my-button first-button" disabled={isDisabled}>
                                <span ref={buttonSpinner} className="button-custom-spinner spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                Redaktə et
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
})

export { EditTaskModal }